import { API_BASE_URL } from "../../../../config";
// SaleReportDetail/index.js — phase 2 UI cleanup: split into tabs, render-only reorg.
// All state/effects/handlers below are unchanged from the pre-split SaleReportDetail.js;
// only the JSX below the single loading/error gate was reorganized into tab components.
import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { getSection } from './saleReportHelpers';
import SummaryTab from './SummaryTab';
import SalesRowsTab from './SalesRowsTab';
import ForgotSaleTab from './ForgotSaleTab';
import AddSaleTab from './AddSaleTab';
import CustomerDeliveryTab from './CustomerDeliveryTab';
import ManageActionsModal from './ManageActionsModal';

// saledata's forgot-sale error responses are {"error": "..."} (SaleReportController's catch
// blocks) — unwrap that so the accounting UI shows the real Thai reason, not raw JSON text.
const extractErrorMessage = async (res) => {
    const text = await res.text();
    try {
        const parsed = JSON.parse(text);
        return parsed?.error || text || `HTTP ${res.status}`;
    } catch (e) {
        return text || `HTTP ${res.status}`;
    }
};

const TABS = [
    { key: 'summary', label: 'สรุปรายงาน' },
    { key: 'salesRows', label: 'รายการขาย' },
    { key: 'forgotSale', label: 'รายงานลืมส่ง' },
    { key: 'addSale', label: 'เพิ่มการขายที่รู้ชัด' },
    { key: 'customerDelivery', label: 'ลูกค้า/Delivery' },
];

const SaleReportDetail = () => {
    const { state } = useLocation();
    const navigate = useNavigate();
    const params = useParams();

    // figure out reportCode from navigation state or URL param or fallback to sale id
    const saleData = state?.saleData;
    const reportCode = useMemo(() => {
        return (
            saleData?.reportCode ||
            state?.reportCode ||
            params?.id || // fallback: existing route might be /saleDataDetail/:id
            saleData?.id
        );
    }, [saleData, state, params]);

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [fullReport, setFullReport] = useState(null);
    // คนขับที่เลือกตอนส่งเงิน (IMD): undefined = ยังโหลด · null = ยังไม่ระบุ/ดึงไม่ได้ · string = ชื่อ
    const [driverName, setDriverName] = useState(undefined);
    const [showCustomers, setShowCustomers] = useState(false);
    const [deliveryCheckerData, setDeliveryCheckerData] = useState([]);
    const [loadingDelivery, setLoadingDelivery] = useState(false);
    const [loadingRoutine, setLoadingRoutine] = useState(false);
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [showManageCard, setShowManageCard] = useState(false);
    const [errorDelivery, setErrorDelivery] = useState('');
    const [editRow, setEditRow] = useState(null);
    const [tempLevel, setTempLevel] = useState('');
    const [salesList, setSalesList] = useState([]);
    const [loadingSalesList, setLoadingSalesList] = useState(false);
    const [salesListExpanded, setSalesListExpanded] = useState(false);
    const [editSaleMsdUid, setEditSaleMsdUid] = useState(null);
    const [editSaleCode, setEditSaleCode] = useState('');
    const [verifyingReport, setVerifyingReport] = useState(false);
    const [deletingSaleMsdUid, setDeletingSaleMsdUid] = useState(null);
    const [addSaleCustomerId, setAddSaleCustomerId] = useState('');
    const [addSaleSelectedIceCode, setAddSaleSelectedIceCode] = useState('');
    const [addSaleQtyInput, setAddSaleQtyInput] = useState('');
    const [addSalePriceInput, setAddSalePriceInput] = useState('');
    const [addSalePendingItems, setAddSalePendingItems] = useState([]);
    const [addingSale, setAddingSale] = useState(false);
    const [forgotSelectedIceCode, setForgotSelectedIceCode] = useState('');
    const [forgotQtyInput, setForgotQtyInput] = useState('');
    const [forgotPendingItems, setForgotPendingItems] = useState([]);
    const [addingForgotSale, setAddingForgotSale] = useState(false);
    const [forgotMode, setForgotMode] = useState('normal'); // 'normal' | 'promo'
    const [forgotPromoPriceInput, setForgotPromoPriceInput] = useState('');
    const [forgotPrices, setForgotPrices] = useState({}); // BASE iceCode ("1I") -> seller's default price
    const [loadingForgotPrices, setLoadingForgotPrices] = useState(false);
    const [forgotPricesError, setForgotPricesError] = useState('');
    const [activeTab, setActiveTab] = useState('summary');

    const handleVerifyReport = async () => {
        const userStr = localStorage.getItem('user');
        const token = userStr ? JSON.parse(userStr)?.token : null;
        if (!token || !reportCode) return;
        setVerifyingReport(true);
        try {
            const url = `${API_BASE_URL}/saledata/api/module/reports/${encodeURIComponent(reportCode)}`;
            const headers = { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` };
            const res = await fetch(url, { method: 'PUT', headers, body: JSON.stringify({ verified: true }) });
            if (!res.ok) { const t = await res.text(); throw new Error(t || `HTTP ${res.status}`); }
            setFullReport(prev => {
                if (!prev) return prev;
                const clone = { ...prev };
                if (clone.masterReport) clone.masterReport = { ...clone.masterReport, verified: true };
                if (clone.master) clone.master = { ...clone.master, verified: true };
                return clone;
            });
        } catch (e) {
            alert('ไม่สามารถปิดรายงานได้: ' + (e.message || ''));
        } finally {
            setVerifyingReport(false);
        }
    };

    const fetchSalesList = async (rc) => {
        const userStr = localStorage.getItem('user');
        const token = userStr ? JSON.parse(userStr)?.token : null;
        if (!token || !rc) return;
        setLoadingSalesList(true);
        try {
            const url = `${API_BASE_URL}/saledata/api/module/reports/${encodeURIComponent(rc)}/sales-list`;
            const headers = { 'Accept': 'application/json', 'Authorization': `Bearer ${token}` };
            const res = await fetch(url, { headers });
            if (res.ok) setSalesList(await res.json());
        } catch (e) {
            console.error('fetchSalesList error:', e);
        } finally {
            setLoadingSalesList(false);
        }
    };

    const handleRecodeSale = async (msdUid, newSaleCode) => {
        const userStr = localStorage.getItem('user');
        const token = userStr ? JSON.parse(userStr)?.token : null;
        if (!token) return;
        try {
            const url = `${API_BASE_URL}/saledata/api/module/sales/${encodeURIComponent(msdUid)}/recode`;
            const headers = { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` };
            const res = await fetch(url, {
                method: 'PUT',
                headers,
                body: JSON.stringify({ sale_code: newSaleCode }),
            });
            if (!res.ok) {
                const txt = await res.text();
                alert('แก้ไขไม่สำเร็จ: ' + txt);
                return;
            }
            setEditSaleMsdUid(null);
            setEditSaleCode('');
            // Full reload (not just the sales-list) so totals/promotions/refills/bag-return
            // sections all reflect the edit too — vector 2026-07-10: these were going stale
            // after a recode until a manual page refresh.
            await fetchFullReport();
            alert('แก้ไขสำเร็จครับ');
        } catch (e) {
            alert('เกิดข้อผิดพลาด: ' + e.message);
        }
    };

    const handleDeleteSale = async (msdUid) => {
        const userStr = localStorage.getItem('user');
        const token = userStr ? JSON.parse(userStr)?.token : null;
        if (!token) return;
        if (!window.confirm('ยืนยันลบรายการขายนี้? การลบไม่สามารถกู้คืนได้')) return;
        setDeletingSaleMsdUid(msdUid);
        try {
            const url = `${API_BASE_URL}/saledata/api/module/sales/${encodeURIComponent(msdUid)}`;
            const headers = { 'Authorization': `Bearer ${token}` };
            const res = await fetch(url, { method: 'DELETE', headers });
            if (!res.ok) {
                const txt = await res.text();
                alert('ลบไม่สำเร็จ: ' + txt);
                return;
            }
            // Full reload so totals/sales list both reflect the deletion.
            await fetchFullReport();
        } catch (e) {
            alert('เกิดข้อผิดพลาด: ' + e.message);
        } finally {
            setDeletingSaleMsdUid(null);
        }
    };

    const addSalePendingItem = (productOptions) => {
        const quantity = parseInt(addSaleQtyInput, 10);
        const unitPrice = parseFloat(addSalePriceInput);
        if (!addSaleSelectedIceCode) {
            alert('เลือกสินค้าก่อน');
            return;
        }
        if (!addSaleQtyInput || Number.isNaN(quantity) || quantity <= 0) {
            alert('กรอกจำนวนให้ถูกต้อง');
            return;
        }
        if (!addSalePriceInput || Number.isNaN(unitPrice) || unitPrice <= 0) {
            alert('กรอกราคาให้ถูกต้อง (ต้องมากกว่า 0)');
            return;
        }
        const product = productOptions.find(p => p.iceCode === addSaleSelectedIceCode);
        setAddSalePendingItems(prev => [
            ...prev,
            { iceCode: addSaleSelectedIceCode, productName: product?.productName || addSaleSelectedIceCode, quantity, unitPrice },
        ]);
        setAddSaleQtyInput('');
        setAddSalePriceInput('');
    };

    const removeAddSalePendingItem = (index) => {
        setAddSalePendingItems(prev => prev.filter((_, i) => i !== index));
    };

    const handleAddSale = async () => {
        const userStr = localStorage.getItem('user');
        const token = userStr ? JSON.parse(userStr)?.token : null;
        if (!token || !reportCode) return;
        if (!addSaleCustomerId.trim()) {
            alert('กรอกรหัสลูกค้าก่อน');
            return;
        }
        if (addSalePendingItems.length === 0) {
            alert('ยังไม่มีรายการที่จะบันทึก');
            return;
        }
        setAddingSale(true);
        try {
            const url = `${API_BASE_URL}/saledata/api/module/reports/${encodeURIComponent(reportCode)}/add-sale`;
            const headers = { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` };
            const items = addSalePendingItems.map(i => ({ iceCode: i.iceCode, quantity: i.quantity, unitPrice: i.unitPrice }));
            const res = await fetch(url, {
                method: 'POST',
                headers,
                body: JSON.stringify({ customerId: addSaleCustomerId.trim(), items }),
            });
            if (!res.ok) {
                const txt = await res.text();
                alert('บันทึกไม่สำเร็จ: ' + txt);
                return;
            }
            setAddSalePendingItems([]);
            setAddSaleCustomerId('');
            setAddSaleSelectedIceCode('');
            setAddSaleQtyInput('');
            setAddSalePriceInput('');
            // Full reload so totalQty/totalPrice and the sales list both reflect the new row.
            await fetchFullReport();
            alert('บันทึกสำเร็จครับ');
        } catch (e) {
            alert('เกิดข้อผิดพลาด: ' + e.message);
        } finally {
            setAddingSale(false);
        }
    };

    const addForgotPendingItem = (productOptions) => {
        const quantity = parseInt(forgotQtyInput, 10);
        if (!forgotSelectedIceCode) {
            alert('เลือกสินค้าก่อน');
            return;
        }
        if (!forgotQtyInput || Number.isNaN(quantity) || quantity <= 0) {
            alert('กรอกจำนวนให้ถูกต้อง');
            return;
        }
        // forgotSelectedIceCode holds the BASE code (dropdown groups by it — see
        // ForgotSaleTab); iceCode kept for submission is the option's representative raw
        // itemsBreakdown code, backend re-derives the real line price from its base regardless.
        const product = productOptions.find(p => p.baseIceCode === forgotSelectedIceCode);
        setForgotPendingItems(prev => [
            ...prev,
            {
                iceCode: product?.iceCode || forgotSelectedIceCode,
                baseIceCode: forgotSelectedIceCode,
                productName: product?.productName || forgotSelectedIceCode,
                quantity,
            },
        ]);
        setForgotQtyInput('');
    };

    const removeForgotPendingItem = (index) => {
        setForgotPendingItems(prev => prev.filter((_, i) => i !== index));
    };

    const fetchForgotSalePrices = async (rc) => {
        const userStr = localStorage.getItem('user');
        const token = userStr ? JSON.parse(userStr)?.token : null;
        if (!token || !rc) return;
        setLoadingForgotPrices(true);
        setForgotPricesError('');
        try {
            const url = `${API_BASE_URL}/saledata/api/module/reports/${encodeURIComponent(rc)}/forgot-sale/prices`;
            const res = await fetch(url, {
                method: 'GET',
                headers: { 'Accept': 'application/json', 'Authorization': `Bearer ${token}` },
            });
            if (!res.ok) {
                const msg = await extractErrorMessage(res);
                throw new Error(msg);
            }
            const data = await res.json();
            setForgotPrices(data && typeof data === 'object' ? data : {});
        } catch (e) {
            console.error('Fetch forgot-sale prices error:', e);
            setForgotPricesError('ไม่สามารถดึงราคาสายได้: ' + (e.message || ''));
            setForgotPrices({});
        } finally {
            setLoadingForgotPrices(false);
        }
    };

    const handleAddForgotSale = async () => {
        const userStr = localStorage.getItem('user');
        const token = userStr ? JSON.parse(userStr)?.token : null;
        if (!token || !reportCode) return;
        if (forgotPendingItems.length === 0) {
            alert('ยังไม่มีรายการที่จะบันทึก');
            return;
        }

        const isPromo = forgotMode === 'promo';
        let promoPrice = null;
        if (isPromo) {
            promoPrice = Number(forgotPromoPriceInput);
            if (forgotPromoPriceInput === '' || Number.isNaN(promoPrice) || promoPrice < 0 || !Number.isInteger(promoPrice)) {
                alert('กรอกราคาโปรโมชั่นเป็นจำนวนเต็มบาท ไม่ติดลบ (0 = ฟรี)');
                return;
            }
        } else {
            const missing = forgotPendingItems.filter(i => forgotPrices[i.baseIceCode] == null);
            if (missing.length > 0) {
                alert('สายนี้ยังไม่ได้ตั้งราคาสินค้า: ' + missing.map(i => i.productName || i.iceCode).join(', '));
                return;
            }
        }

        setAddingForgotSale(true);
        try {
            const url = `${API_BASE_URL}/saledata/api/module/reports/${encodeURIComponent(reportCode)}/forgot-sale`;
            const headers = { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` };
            const items = forgotPendingItems.map(i => ({ iceCode: i.iceCode, quantity: i.quantity }));
            const body = { items, promotion: isPromo, promoPrice: isPromo ? promoPrice : undefined };
            const res = await fetch(url, { method: 'POST', headers, body: JSON.stringify(body) });
            if (!res.ok) {
                const msg = await extractErrorMessage(res);
                alert('บันทึกไม่สำเร็จ: ' + msg);
                return;
            }
            setForgotPendingItems([]);
            setForgotSelectedIceCode('');
            setForgotQtyInput('');
            setForgotPromoPriceInput('');
            // Full reload so totalQty/totalPrice and the sales list both reflect the new row.
            await fetchFullReport();
        } catch (e) {
            alert('เกิดข้อผิดพลาด: ' + e.message);
        } finally {
            setAddingForgotSale(false);
        }
    };

    const fetchDeliveryChecker = async (sId, d) => {
        const userStr = localStorage.getItem('user');
        const token = userStr ? JSON.parse(userStr)?.token : null;
        if (!token) return;

        setLoadingDelivery(true);
        setErrorDelivery('');
        try {
            const url = `${API_BASE_URL}/customer/api/customer-delivery-checker/check/user`;
            const res = await fetch(url, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                },
                body: JSON.stringify({
                    sellerId: sId,
                    date: d
                }),
            });
            if (!res.ok) {
                const text = await res.text();
                throw new Error(text || `HTTP ${res.status}`);
            }
            const data = await res.json();
            console.log("[DEBUG_LOG] Delivery Checker API Response:", data);
            // Sort data to put NO_DATA at the top
            const sortedData = Array.isArray(data) ? [...data].sort((a, b) => {
                if (a.status === 'NO_DATA' && b.status !== 'NO_DATA') return -1;
                if (a.status !== 'NO_DATA' && b.status === 'NO_DATA') return 1;
                return 0;
            }) : [data];
            setDeliveryCheckerData(sortedData);
        } catch (e) {
            console.error('Fetch delivery checker error:', e);
            setErrorDelivery('เกิดข้อผิดพลาดในการตรวจสอบการส่ง: ' + (e.message || ''));
        } finally {
            setLoadingDelivery(false);
        }
    };

    const handleTriggerRoutine = async (sId, sd, ed) => {
        const userStr = localStorage.getItem('user');
        const token = userStr ? JSON.parse(userStr)?.token : null;
        if (!token) return;

        setLoadingRoutine(true);
        try {
            const url = `${API_BASE_URL}/customer/api/customer-buy-rutine/fetch`;
            const response = await fetch(url, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                },
                body: JSON.stringify({
                    sellerId: sId,
                    startDate: sd,
                    endDate: ed
                }),
            });

            if (!response.ok) {
                const text = await response.text();
                alert(`Failed to fetch routine: ${text}`);
            } else {
                const data = await response.json();
                alert(`Routine updated successfully for ${sId}. Found ${data.length} customers.`);
            }
        } catch (error) {
            console.error('Routine fetch error:', error);
            alert('Error triggering routine');
        } finally {
            setLoadingRoutine(false);
        }
    };

    const handleUpdateDeliveryLevel = async (customerId, sId, level) => {
        const userStr = localStorage.getItem('user');
        const token = userStr ? JSON.parse(userStr)?.token : null;
        if (!token) return;

        try {
            const url = `${API_BASE_URL}/customer/api/customer-buy-rutine/delivery-level/update`;
            const res = await fetch(url, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                },
                body: JSON.stringify({
                    customerId: customerId,
                    sellerId: sId,
                    deliveryLevel: Number(level)
                }),
            });
            if (!res.ok) {
                const text = await res.text();
                throw new Error(text || `HTTP ${res.status}`);
            }
            const data = await res.json();
            alert('Updated delivery level successfully');
            // Update local state if needed
            setDeliveryCheckerData(prev => prev.map(item => {
                if (item.customerId === customerId) {
                    const newItem = { ...item, deliveryLevel: data.deliveryLevel };
                    delete newItem._tempLevel;
                    return newItem;
                }
                return item;
            }));
        } catch (e) {
            console.error('Update delivery level error:', e);
            alert('เกิดข้อผิดพลาดในการอัปเดตระดับการส่ง: ' + (e.message || ''));
        }
    };

    // quiet = รีโหลดหลังแก้รายการ: ไม่ตั้ง loading เพื่อไม่ให้ทั้งหน้าถูกแทนด้วย "กำลังโหลดข้อมูล…" (แผงรายการกระสอบจะหาย/ปิดตัว)
    const fetchFullReport = async ({ quiet = false } = {}) => {
            const userStr = localStorage.getItem('user');
            if (!userStr) {
                navigate('/login');
                return;
            }
            const token = JSON.parse(userStr)?.token;
            if (!token) {
                navigate('/login');
                return;
            }
            if (!reportCode) {
                setError('ไม่พบรหัสรายงาน (reportCode)');
                setLoading(false);
                return;
            }
            if (!quiet) setLoading(true);
            setError('');
            try {
                const url = `${API_BASE_URL}/saledata/api/module/reports/${encodeURIComponent(reportCode)}/full`;
                const headers = {
                    'Accept': 'application/json',
                    'Authorization': `Bearer ${token}`,
                };
                const res = await fetch(url, { method: 'GET', headers });
                if (!res.ok) {
                    const text = await res.text();
                    throw new Error(text || `HTTP ${res.status}`);
                }
                const data = await res.json();
                console.log("saleReportData :", data); // Debugging line

                // Redirect agent sales to dedicated page
                const master0 = getSection(data, ['master', 'masterReport', 'report', 'masterData'], null);
                if (master0?.isAgent === true) {
                    navigate(`/AgentsaleDataDetail/${encodeURIComponent(reportCode)}`, { replace: true });
                    return;
                }

                setFullReport(data);

                const master = getSection(data, ['master', 'masterReport', 'report', 'masterData'], null);
                if (master && master.date) {
                    setStartDate(master.date);
                    setEndDate(master.date);
                    // Automatically trigger delivery check on load
                    fetchDeliveryChecker(master.sellerId, master.date);
                }
                fetchSalesList(reportCode);
                fetchForgotSalePrices(reportCode);
            } catch (e) {
                console.error('Fetch full report error:', e);
                setError('เกิดข้อผิดพลาดในการดึงรายงานแบบเต็ม: ' + (e.message || ''));
            } finally {
                setLoading(false);
            }
    };

    useEffect(() => {
        fetchFullReport();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [reportCode]);

    useEffect(() => {
        let cancelled = false;
        setDriverName(undefined);
        (async () => {
            try {
                const token = JSON.parse(localStorage.getItem('user') || 'null')?.token;
                if (!token || !reportCode) return;
                const res = await fetch(`${API_BASE_URL}/seller/api/tablet/report/web/rounds/${encodeURIComponent(reportCode)}`, {
                    headers: { Accept: 'application/json', Authorization: `Bearer ${token}` },
                });
                if (!res.ok) throw new Error(`web round ${res.status}`);
                const data = await res.json();
                if (!cancelled) setDriverName(data?.driverNickname || (data?.driverEmployeeId ? 'ระบุแล้ว (ไม่พบชื่อ)' : null));
            } catch (e) {
                console.error('driver name load failed', e);
                if (!cancelled) setDriverName(null);
            }
        })();
        return () => { cancelled = true; };
    }, [reportCode]);

    const renderMaster = (master) => {
        if (!master) return <p>ไม่พบข้อมูลสรุปหลัก</p>;
        return (
            <div className="btc-summary-card" style={{ gridColumn: 'span 2' }}>
                <div className="btc-summary-item">
                    <span className="btc-summary-label">สายการขาย (Seller)</span>
                    <span className="btc-summary-value">{fullReport?.masterReport?.sellerName || master.sellerId}</span>
                </div>
                <div className="btc-summary-item">
                    <span className="btc-summary-label">วันที่ (Date)</span>
                    <span className="btc-summary-value">{master.date}</span>
                </div>
                <div className="btc-summary-item">
                    <span className="btc-summary-label">คนขับ (Driver)</span>
                    <span className="btc-summary-value" data-testid="round-driver-name">{driverName === undefined ? '...' : (driverName || 'ยังไม่ระบุคนขับ')}</span>
                </div>
                <div className="btc-summary-item">
                    <span className="btc-summary-label">รายได้รวม (Total Price)</span>
                    <span className="btc-summary-value">{Number(master.totalPrice).toLocaleString()} ฿</span>
                </div>
                <div className="btc-summary-item">
                    <span className="btc-summary-label">จำนวนร้าน (Shops)</span>
                    <span className="btc-summary-value">{master.shopCount}</span>
                </div>
                <div className="btc-summary-item">
                    <span className="btc-summary-label">ยอดถุงรวม (Bags)</span>
                    <span className="btc-summary-value">{master.totalQty}</span>
                </div>
                <div className="btc-summary-item">
                    <button
                        className="btc-btn btn-sm"
                        style={{ backgroundColor: "rgba(255,255,255,0.2)", border: "1px solid #fff" }}
                        onClick={() => setShowManageCard(true)}
                    >
                        จัดการรายงาน
                    </button>
                </div>
                <div className="btc-summary-item">
                    {master.verified
                        ? <button disabled className="btc-btn btn-sm" style={{ backgroundColor: '#28a745' }}>✅ ตรวจสอบแล้ว</button>
                        : <button className="btc-btn btn-sm" style={{ backgroundColor: '#28a745' }} onClick={handleVerifyReport} disabled={verifyingReport}>
                            {verifyingReport ? 'กำลังบันทึก...' : 'ตรวจสอบแล้ว'}
                          </button>
                    }
                </div>
            </div>
        );
    };

    const handleBack = () => navigate('/SaleData-management');

    if (!state && !params?.id) {
        // No context at all; go back to list
        // But let it render a message first in case navigation fails
    }

    // Render-only derived data below — same single FORGOT-sentinel split as before this
    // brief, just computed once here so SalesRowsTab/ForgotSaleTab can't drift apart on
    // what counts as a "normal" vs "forgot" sale row (point 4 of the phase-2 brief).
    const normalSalesRows = salesList.filter(s => s.customerId !== 'FORGOT');
    const forgotSalesRows = salesList.filter(s => s.customerId === 'FORGOT');

    const renderActiveTab = () => {
        switch (activeTab) {
            case 'salesRows':
                return (
                    <SalesRowsTab
                        fullReport={fullReport}
                        reportCode={reportCode}
                        salesRows={normalSalesRows}
                        deliveryCheckerData={deliveryCheckerData}
                        editSaleMsdUid={editSaleMsdUid}
                        setEditSaleMsdUid={setEditSaleMsdUid}
                        editSaleCode={editSaleCode}
                        setEditSaleCode={setEditSaleCode}
                        deletingSaleMsdUid={deletingSaleMsdUid}
                        salesListExpanded={salesListExpanded}
                        setSalesListExpanded={setSalesListExpanded}
                        loadingSalesList={loadingSalesList}
                        handleRecodeSale={handleRecodeSale}
                        handleDeleteSale={handleDeleteSale}
                    />
                );
            case 'forgotSale':
                return (
                    <ForgotSaleTab
                        fullReport={fullReport}
                        forgotSalesRows={forgotSalesRows}
                        forgotSelectedIceCode={forgotSelectedIceCode}
                        setForgotSelectedIceCode={setForgotSelectedIceCode}
                        forgotQtyInput={forgotQtyInput}
                        setForgotQtyInput={setForgotQtyInput}
                        forgotPendingItems={forgotPendingItems}
                        addForgotPendingItem={addForgotPendingItem}
                        removeForgotPendingItem={removeForgotPendingItem}
                        handleAddForgotSale={handleAddForgotSale}
                        addingForgotSale={addingForgotSale}
                        forgotMode={forgotMode}
                        setForgotMode={setForgotMode}
                        forgotPromoPriceInput={forgotPromoPriceInput}
                        setForgotPromoPriceInput={setForgotPromoPriceInput}
                        forgotPrices={forgotPrices}
                        loadingForgotPrices={loadingForgotPrices}
                        forgotPricesError={forgotPricesError}
                    />
                );
            case 'addSale':
                return (
                    <AddSaleTab
                        fullReport={fullReport}
                        addSaleCustomerId={addSaleCustomerId}
                        setAddSaleCustomerId={setAddSaleCustomerId}
                        addSaleSelectedIceCode={addSaleSelectedIceCode}
                        setAddSaleSelectedIceCode={setAddSaleSelectedIceCode}
                        addSaleQtyInput={addSaleQtyInput}
                        setAddSaleQtyInput={setAddSaleQtyInput}
                        addSalePriceInput={addSalePriceInput}
                        setAddSalePriceInput={setAddSalePriceInput}
                        addSalePendingItems={addSalePendingItems}
                        addSalePendingItem={addSalePendingItem}
                        removeAddSalePendingItem={removeAddSalePendingItem}
                        handleAddSale={handleAddSale}
                        addingSale={addingSale}
                    />
                );
            case 'customerDelivery':
                return (
                    <CustomerDeliveryTab
                        fullReport={fullReport}
                        deliveryCheckerData={deliveryCheckerData}
                        loadingDelivery={loadingDelivery}
                        errorDelivery={errorDelivery}
                        startDate={startDate}
                        fetchDeliveryChecker={fetchDeliveryChecker}
                        editRow={editRow}
                        setEditRow={setEditRow}
                        tempLevel={tempLevel}
                        setTempLevel={setTempLevel}
                        handleUpdateDeliveryLevel={handleUpdateDeliveryLevel}
                        showCustomers={showCustomers}
                        setShowCustomers={setShowCustomers}
                    />
                );
            case 'summary':
            default:
                return <SummaryTab fullReport={fullReport} onReload={() => fetchFullReport({ quiet: true })} />;
        }
    };

    return (
        <div className="btc-container">
            <div className="btc-card" style={{width: "auto"}}>
                <h1>รายงานการขาย {fullReport?.masterReport?.sellerName ? `${fullReport.masterReport.sellerName}` : ''} (แบบเต็ม)</h1>
                <div className="btc-content">
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                        <button onClick={handleBack} className="btc-btn" style={{ backgroundColor: '#6c757d' }}>กลับ</button>
                        <div style={{ display: 'flex', gap: '10px' }}>
                             {/* Space for more actions if needed */}
                        </div>
                    </div>
                    {loading && <p>กำลังโหลดข้อมูล...</p>}
                    {error && <p className="error">{error}</p>}
                    {!loading && !error && fullReport && (
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: '20px' }}>
                            {renderMaster(getSection(fullReport, ['master', 'masterReport', 'report', 'masterData'], null))}

                            <div style={{ ...tabBar, gridColumn: 'span 2' }}>
                                {TABS.map(tab => (
                                    <button
                                        key={tab.key}
                                        type="button"
                                        style={activeTab === tab.key ? tabButtonActive : tabButton}
                                        onClick={() => setActiveTab(tab.key)}
                                    >
                                        {tab.label}
                                    </button>
                                ))}
                            </div>

                            {renderActiveTab()}
                        </div>
                    )}
                    {!loading && !error && !fullReport && (
                        <p>ไม่พบข้อมูลรายงาน</p>
                    )}
                </div>
            </div>

            {/* Manage Actions Modal */}
            {showManageCard && fullReport && (
                <ManageActionsModal
                    fullReport={fullReport}
                    startDate={startDate}
                    setStartDate={setStartDate}
                    endDate={endDate}
                    setEndDate={setEndDate}
                    loadingDelivery={loadingDelivery}
                    loadingRoutine={loadingRoutine}
                    fetchDeliveryChecker={fetchDeliveryChecker}
                    handleTriggerRoutine={handleTriggerRoutine}
                    onClose={() => setShowManageCard(false)}
                />
            )}
        </div>
    );
};

// Tab bar — matches the existing plain-styled-button pattern from BagLedgerAccounting.js
// (bag-ledger page, feature/bagledger-split-frontend): transparent buttons, active tab
// gets a bottom border + bold + accent color. No UI library.
const tabBar = { display: "flex", gap: 4, borderBottom: "2px solid #d9e1ea", marginBottom: 4 };
const tabButton = { background: "transparent", border: 0, borderBottom: "3px solid transparent", padding: "10px 16px", cursor: "pointer", fontWeight: 600, fontSize: 14, color: "#64748b", marginBottom: -2 };
const tabButtonActive = { ...tabButton, color: "#d71920", borderBottom: "3px solid #d71920", fontWeight: 700 };

export default SaleReportDetail;
